import React, { useState } from 'react';
import { useFloorLadder, useFloorDepth, useListingsAgeStats } from '../../hooks/useApi/listings';
import { usePriceImpact } from '../../hooks/useApi/signals';
import DataTable from './shared/DataTable';
import KPICard from './shared/KPICard';
import ErrorBoundary from './shared/ErrorBoundary';

const FloorDepthTab = () => {
  const [priceImpactTake, setPriceImpactTake] = useState('');
  
  const { data: floorLadder, isLoading: ladderLoading, isError: ladderError } = useFloorLadder(50);
  const { data: floorDepth, isLoading: depthLoading, isError: depthError } = useFloorDepth('5,10,20');
  const { data: ageStats, isLoading: ageLoading, isError: ageError } = useListingsAgeStats();
  const { data: priceImpact, isLoading: priceImpactLoading, isError: priceImpactError, calculateImpact } = usePriceImpact();

  const formatXTZ = (mutez) => {
    if (!mutez) return '0';
    return (mutez / 1000000).toFixed(2);
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return 'Just now';
  };

  const handlePriceImpactCalculate = () => {
    const take = parseInt(priceImpactTake);
    if (take && take > 0) {
      console.log('Calculating price impact for:', take, 'NFTs');
      calculateImpact(take);
    }
  };

  // Debug price impact data
  React.useEffect(() => {
    if (priceImpact) {
      console.log('Price impact data received:', priceImpact);
    }
  }, [priceImpact]);

  // Get proper image URL - same logic as rarity tool
  const getImageUrl = (token) => {
    const nftNumber = token.token_name?.match(/#(\d+)/)?.[1] || token.token_id;
    
    if (token.display_uri) {
      // Convert ipfs:// to https://ipfs.io/ipfs/
      if (token.display_uri.startsWith('ipfs://')) {
        const ipfsUrl = token.display_uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
        return ipfsUrl;
      }
      
      // Handle other URI formats
      return token.display_uri;
    }
    
    // Generate beautiful placeholder for tokens without display_uri
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="200" fill="#1a1a1a"/>
        <rect width="180" height="180" x="10" y="10" fill="#2a2a2a" rx="15"/>
        <circle cx="100" cy="70" r="25" fill="#4a4a4a"/>
        <text x="100" y="78" font-family="Arial" font-size="12" fill="#888" text-anchor="middle">NFT People</text>
        <text x="100" y="120" font-family="Arial" font-size="28" fill="#fff" text-anchor="middle" font-weight="bold">#${nftNumber}</text>
        <text x="100" y="150" font-family="Arial" font-size="10" fill="#666" text-anchor="middle">No Image</text>
      </svg>
    `)}`;
  };


  const ladderColumns = [
    {
      key: 'image',
      header: 'Image',
      render: (value, row) => (
        <div className="token-image">
          <img 
            src={getImageUrl(row)} 
            alt={`Token ${row.token_id}`}
            onError={(e) => {
              const nftNumber = row.token_name?.match(/#(\d+)/)?.[1] || row.token_id;
              e.target.src = `data:image/svg+xml;base64,${btoa(`
                <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
                  <rect width="200" height="200" fill="#1a1a1a"/>
                  <rect width="180" height="180" x="10" y="10" fill="#2a2a2a" rx="15"/>
                  <circle cx="100" cy="70" r="25" fill="#4a4a4a"/>
                  <text x="100" y="78" font-family="Arial" font-size="12" fill="#888" text-anchor="middle">NFT People</text>
                  <text x="100" y="120" font-family="Arial" font-size="28" fill="#fff" text-anchor="middle" font-weight="bold">#${nftNumber}</text>
                  <text x="100" y="150" font-family="Arial" font-size="10" fill="#666" text-anchor="middle">No Image</text>
                </svg>
              `)}`;
            }}
          />
        </div>
      )
    },
    {
      key: 'token_name',
      header: 'Token',
      render: (value, row) => (
        <div>
          <div className="token-name">{value || `#${row.token_id}`}</div>
          <div className="token-id">#{row.token_id}</div>
        </div>
      )
    },
    {
      key: 'price',
      header: 'Price',
      render: (value) => `${formatXTZ(value)} XTZ`
    },
    {
      key: 'timestamp',
      header: 'Listed',
      render: (value) => formatRelativeTime(value)
    },
    {
      key: 'traits',
      header: 'Traits',
      render: (value) => (
        <div className="trait-badges">
          {value && Object.entries(value).slice(0, 3).map(([key, val], index) => (
            <span key={index} className="trait-badge">
              {key}: {val}
            </span>
          ))}
          {value && Object.keys(value).length > 3 && (
            <span className="trait-badge more">+{Object.keys(value).length - 3}</span>
          )}
        </div>
      )
    }
  ];


  return (
    <ErrorBoundary>
      <div className="floor-depth-tab">
        {/* Header with Current Floor */}
        <div className="floor-header">
          <h2>Floor & Depth Analysis</h2>
          {floorDepth?.floor && (
            <div className="current-floor-display">
              <span className="floor-label">Current Floor:</span>
              <span className="floor-price">{formatXTZ(floorDepth.floor)} XTZ</span>
            </div>
          )}
        </div>

        {/* Market Depth Section */}
        <div className="market-depth-section">
          <h3>Market Depth</h3>
          <div className="depth-cards-grid">
          {floorDepth?.depths && floorDepth.depths.map((depth) => (
              <div key={depth.pct} className="depth-card">
                <div className="depth-header">
                  <div className="depth-percentage">+{depth.pct}%</div>
                  <div className="depth-label">Depth</div>
                </div>
                <div className="depth-value">{depth.count ? depth.count.toLocaleString() : '0'}</div>
                <div className="depth-subtitle">{formatXTZ(depth.notional)} XTZ notional</div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="floor-content-grid">
          {/* Left Column - Age Statistics */}
          <div className="age-stats-column">
            <div className="age-stats-section">
              <h3>Listing Age Statistics</h3>
              <div className="age-stats-grid">
                <div className="age-stat-card">
                  <div className="age-stat-header">
                    <span className="age-label">Median Age</span>
                  </div>
                  <span className="age-value">{ageStats?.medianDays ? `${ageStats.medianDays.toFixed(1)} days` : 'Loading...'}</span>
                </div>
                <div className="age-stat-card">
                  <div className="age-stat-header">
                    <span className="age-label">Mean Age</span>
                  </div>
                  <span className="age-value">{ageStats?.meanDays ? `${ageStats.meanDays.toFixed(1)} days` : 'Loading...'}</span>
                </div>
                <div className="age-stat-card">
                  <div className="age-stat-header">
                    <span className="age-label">Older than 7 days</span>
                  </div>
                  <span className="age-value">{ageStats?.pctOld?.['>7d'] ? `${ageStats.pctOld['>7d'].toFixed(1)}%` : 'Loading...'}</span>
                </div>
                <div className="age-stat-card">
                  <div className="age-stat-header">
                    <span className="age-label">Older than 30 days</span>
                  </div>
                  <span className="age-value">{ageStats?.pctOld?.['>30d'] ? `${ageStats.pctOld['>30d'].toFixed(1)}%` : 'Loading...'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Floor Ladder */}
          <div className="floor-ladder">
            <div className="ladder-header">
              <div className="ladder-title-section">
                <h3>Floor Ladder</h3>
                <span className="ladder-subtitle">50 Cheapest Listings</span>
              </div>
              <div className="ladder-stats">
                <div className="ladder-stat">
                  <span className="stat-label">Total Value</span>
                  <span className="stat-value">
                    {floorLadder ? formatXTZ(floorLadder.reduce((sum, item) => sum + (item.price || 0), 0)) : '0'} XTZ
                  </span>
                </div>
              </div>
            </div>
            <div className="ladder-table-container">
          <DataTable
            columns={ladderColumns}
            data={floorLadder || []}
            loading={ladderLoading}
            emptyMessage="No floor listings available"
            className="floor-ladder-table"
          />
        </div>
          </div>
        </div>

      </div>
    </ErrorBoundary>
  );
};

export default FloorDepthTab;
